import { useState, useRef, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useLocation } from 'react-router-dom'
import {
  DocumentTextIcon,
  BanknotesIcon,
  CreditCardIcon,
  MagnifyingGlassIcon,
  CalendarIcon,
  ArrowDownTrayIcon,
  PrinterIcon,
  XMarkIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  UserIcon,
} from '@heroicons/react/24/outline'
import { reportService } from '../../services/reportService'
import { customerService } from '../../services/customerService'
import { settingService } from '../../services/settingService'

interface StatementLine {
  type: 'invoice' | 'payment'
  date: string
  reference: string
  description: string
  debit: number
  credit: number
  balance: number
  status: string
  method?: string
  customer: string
}

interface PaymentMethodBreakdown {
  method: string
  count: number
  amount: number
}

interface Customer {
  id: number
  name: string
  code: string | null
  email: string | null
  phone: string | null
  creditLimit: number
  group: string
}

interface StatementSummary {
  totalInvoices: number
  totalInvoiceAmount: number
  totalPaid: number
  totalCredits: number
  balanceDue: number
  paymentMethods: PaymentMethodBreakdown[]
}

export const StatementsPage = () => {
  const location = useLocation()
  const preselectedCustomerId = (location.state as any)?.customerId as number | undefined

  const [selectedCustomerId, setSelectedCustomerId] = useState<number | ''>(preselectedCustomerId || '')
  const [startDate, setStartDate] = useState(() => {
    const d = new Date()
    // Go back 2 years when pre-selected from debtors, otherwise 1 year
    d.setFullYear(d.getFullYear() - (preselectedCustomerId ? 2 : 1))
    return d.toISOString().split('T')[0]
  })
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0])
  const [search, setSearch] = useState('')
  const [showAllDropdown, setShowAllDropdown] = useState(false)
  const [showPdfPreview, setShowPdfPreview] = useState(false)
  const [companySettings, setCompanySettings] = useState<any>(null)

  const printRef = useRef<HTMLDivElement>(null)

  // Fetch customers for dropdown
  const { data: customersData } = useQuery({
    queryKey: ['customers-list'],
    queryFn: () => customerService.getAll({ limit: 500 }),
  })

  const customers: Customer[] = (customersData?.data?.data?.items || []).map((c: any) => ({
    id: c.customer_id,
    name: c.company_name || `${c.first_name || ''} ${c.last_name || ''}`.trim(),
    code: c.customer_code,
    email: c.email,
    phone: c.phone,
    creditLimit: Number(c.credit_limit || 0),
    group: c.customer_groups?.group_name || 'Ungrouped',
  }))

  const filteredCustomers = customers.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    (c.code && c.code.toLowerCase().includes(search.toLowerCase()))
  )

  // Pre-populate search when navigated from another page with a preselected customer
  useEffect(() => {
    if (preselectedCustomerId && customers.length > 0) {
      const customer = customers.find((c) => c.id === preselectedCustomerId)
      if (customer) {
        setSearch(customer.name)
      }
    }
  }, [preselectedCustomerId, customers])

  // Fetch statement
  const { data, isLoading } = useQuery({
    queryKey: ['statement', selectedCustomerId, startDate, endDate],
    queryFn: () =>
      reportService.getCustomerStatement({
        customer_id: selectedCustomerId ? Number(selectedCustomerId) : undefined,
        startDate,
        endDate,
      }),
    enabled: !!selectedCustomerId,
  })

  const report = data?.data?.data
  const summary: StatementSummary = report?.summary || {
    totalInvoices: 0,
    totalInvoiceAmount: 0,
    totalPaid: 0,
    totalCredits: 0,
    balanceDue: 0,
    paymentMethods: [],
  }
  const statement: StatementLine[] = report?.statement || []
  const selectedCustomer = customers.find((c) => c.id === Number(selectedCustomerId))

  useEffect(() => {
    if (showPdfPreview) {
      fetchCompanySettings()
    }
  }, [showPdfPreview])

  const fetchCompanySettings = async () => {
    try {
      const response = await settingService.getCompanySettings()
      if (response.data?.data) {
        setCompanySettings(response.data.data)
      }
    } catch {
      // ignore
    }
  }

  const formatCurrency = (amount: number) => {
    return `KSh ${Number(amount).toLocaleString()}`
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  }

  const handleExportCSV = () => {
    if (!statement.length) return

    const headers = ['Date', 'Reference', 'Description', 'Debit', 'Credit', 'Balance', 'Status']
    const rows = statement.map((line) => [
      formatDate(line.date),
      line.reference,
      line.description,
      line.debit > 0 ? line.debit.toString() : '',
      line.credit > 0 ? line.credit.toString() : '',
      line.balance.toString(),
      line.status,
    ])

    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n')

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `statement-${selectedCustomer?.name || 'all'}-${startDate}-to-${endDate}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handlePrint = () => {
    window.print()
  }

  const widgets = [
    {
      title: 'Total Invoices',
      value: summary.totalInvoices.toString(),
      sub: formatCurrency(summary.totalInvoiceAmount),
      icon: DocumentTextIcon,
      color: 'bg-blue-100 text-blue-600',
    },
    {
      title: 'Total Paid',
      value: formatCurrency(summary.totalPaid),
      sub: `${summary.paymentMethods.length} methods`,
      icon: BanknotesIcon,
      color: 'bg-green-100 text-green-600',
    },
    {
      title: 'Balance Due',
      value: formatCurrency(summary.balanceDue),
      sub: summary.balanceDue > 0 ? 'Outstanding' : 'Settled',
      icon: CreditCardIcon,
      color: summary.balanceDue > 0 ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600',
    },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Customer Statements</h1>
          <p className="text-sm text-gray-500 mt-1">
            View and export customer transaction statements
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow border border-gray-100 p-4 space-y-4">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Customer Search */}
          <div className="flex-1 relative flex gap-2">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search customers..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setShowAllDropdown(false) }}
                onFocus={() => setShowAllDropdown(false)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
              {(search && filteredCustomers.length > 0 || showAllDropdown) && !selectedCustomerId && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                  {(showAllDropdown ? customers : filteredCustomers).map((customer) => (
                    <button
                      key={customer.id}
                      onClick={() => {
                        setSelectedCustomerId(customer.id)
                        setSearch(customer.name)
                        setShowAllDropdown(false)
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-0"
                    >
                      <p className="text-sm font-medium text-gray-900">{customer.name}</p>
                      <p className="text-xs text-gray-500">{customer.code} • {customer.group}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={() => { setShowAllDropdown(v => !v); setSearch('') }}
              className={`px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${
                showAllDropdown
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              All
            </button>
          </div>

          {/* Selected customer badge */}
          {selectedCustomer && (
            <div className="flex items-center gap-2 px-3 py-2 bg-primary-50 border border-primary-200 rounded-lg">
              <UserIcon className="w-4 h-4 text-primary-600" />
              <span className="text-sm font-medium text-primary-800">{selectedCustomer.name}</span>
              <button
                onClick={() => {
                  setSelectedCustomerId('')
                  setSearch('')
                }}
                className="text-primary-600 hover:text-primary-800"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Date Range */}
          <div className="flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-gray-400" />
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
            />
            <span className="text-gray-500">to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
            />
          </div>
        </div>
      </div>

      {selectedCustomerId && (
        <>
          {/* Widgets */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {widgets.map((widget) => (
              <div key={widget.title} className="bg-white rounded-xl shadow border border-gray-100 p-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${widget.color}`}>
                    <widget.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{widget.title}</p>
                    <p className="text-lg font-semibold text-gray-900">{widget.value}</p>
                    <p className="text-xs text-gray-500">{widget.sub}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Payment Methods Breakdown */}
          {summary.paymentMethods.length > 0 && (
            <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Payment Methods</h3>
              <div className="flex flex-wrap gap-3">
                {summary.paymentMethods.map((pm) => (
                  <div key={pm.method} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
                    <CreditCardIcon className="w-4 h-4 text-gray-500" />
                    <div>
                      <p className="text-xs font-medium text-gray-900">{pm.method}</p>
                      <p className="text-xs text-gray-500">{pm.count} payments • {formatCurrency(pm.amount)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button
              onClick={handleExportCSV}
              disabled={!statement.length}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
              Export CSV
            </button>
            <button
              onClick={() => setShowPdfPreview(true)}
              disabled={!statement.length}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              <PrinterIcon className="w-4 h-4" />
              Print / PDF
            </button>
          </div>

          {/* Statement Table */}
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
          ) : statement.length === 0 ? (
            <div className="bg-white rounded-xl shadow border border-gray-100 p-12 text-center">
              <DocumentTextIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-1">No transactions found</h3>
              <p className="text-gray-500">Try adjusting the date range or select a different customer.</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Debit</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Credit</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {statement.map((line, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                          {formatDate(line.date)}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">
                          {line.reference}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {line.description}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right whitespace-nowrap">
                          {line.debit > 0 ? formatCurrency(line.debit) : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right whitespace-nowrap">
                          {line.credit > 0 ? formatCurrency(line.credit) : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right whitespace-nowrap">
                          {formatCurrency(line.balance)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                              line.type === 'invoice'
                                ? line.status === 'paid'
                                  ? 'bg-green-100 text-green-800'
                                  : line.status === 'overdue'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-blue-100 text-blue-800'
                                : 'bg-green-100 text-green-800'
                            }`}
                          >
                            {line.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* PDF Preview Modal */}
      {showPdfPreview && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowPdfPreview(false)} />
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <div className="relative bg-white rounded-lg shadow-xl max-w-7xl w-full">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 print:hidden">
                  <h2 className="text-lg font-semibold text-gray-900">Statement Preview</h2>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handlePrint}
                      className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                    >
                      <PrinterIcon className="w-4 h-4" />
                      Print
                    </button>
                    <button onClick={() => setShowPdfPreview(false)} className="text-gray-400 hover:text-gray-600">
                      <XMarkIcon className="w-6 h-6" />
                    </button>
                  </div>
                </div>

                {/* Printable Document */}
                <div className="p-6 overflow-auto max-h-[80vh] print:max-h-none print:overflow-visible">
                  <div
                    ref={printRef}
                    className="mx-auto bg-white print:shadow-none"
                    style={{
                      width: '297mm',
                      minHeight: '210mm',
                      padding: '15mm',
                      boxShadow: '0 0 10px rgba(0,0,0,0.1)',
                    }}
                  >
                    {/* Company Header */}
                    <div className="flex justify-between items-start mb-8">
                      <div>
                        {companySettings?.logo && (
                          <img
                            src={companySettings.logo}
                            alt="Company Logo"
                            className="h-16 object-contain mb-2"
                          />
                        )}
                        <h1 className="text-2xl font-bold text-gray-900">
                          {companySettings?.name || 'Aperture Camera Store'}
                        </h1>
                        <div className="text-sm text-gray-600 mt-1 space-y-1">
                          {companySettings?.address && (
                            <div className="flex items-center gap-1">
                              <MapPinIcon className="w-4 h-4" />
                              {companySettings.address}
                            </div>
                          )}
                          {companySettings?.phone && (
                            <div className="flex items-center gap-1">
                              <PhoneIcon className="w-4 h-4" />
                              {companySettings.phone}
                            </div>
                          )}
                          {companySettings?.email && (
                            <div className="flex items-center gap-1">
                              <EnvelopeIcon className="w-4 h-4" />
                              {companySettings.email}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <h2 className="text-3xl font-bold text-gray-400 uppercase tracking-wider">
                          Statement
                        </h2>
                        <p className="text-sm text-gray-600 mt-1">
                          Period: {formatDate(startDate)} - {formatDate(endDate)}
                        </p>
                      </div>
                    </div>

                    {/* Customer Info */}
                    <div className="flex justify-between mb-8">
                      <div className="w-1/2 pr-4">
                        <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Bill To:</h3>
                        <div className="border border-gray-200 rounded-lg p-4">
                          <p className="font-semibold text-gray-900">{selectedCustomer?.name}</p>
                          {selectedCustomer?.email && (
                            <p className="text-gray-600 mt-1">{selectedCustomer.email}</p>
                          )}
                          {selectedCustomer?.phone && (
                            <p className="text-gray-600 mt-1">{selectedCustomer.phone}</p>
                          )}
                          <p className="text-gray-600 mt-1">Group: {selectedCustomer?.group}</p>
                        </div>
                      </div>
                      <div className="w-1/2 pl-4">
                        <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Summary:</h3>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Total Invoices:</span>
                            <span className="font-medium">{summary.totalInvoices}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Total Billed:</span>
                            <span className="font-medium">{formatCurrency(summary.totalInvoiceAmount)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Total Paid:</span>
                            <span className="font-medium">{formatCurrency(summary.totalPaid)}</span>
                          </div>
                          <div className="flex justify-between py-2 border-t-2 border-gray-900">
                            <span className="text-lg font-bold text-gray-900">Balance Due:</span>
                            <span className="text-lg font-bold text-gray-900">{formatCurrency(summary.balanceDue)}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Statement Table */}
                    <table className="w-full mb-8">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                          <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">Reference</th>
                          <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">Description</th>
                          <th className="py-3 px-4 text-right text-xs font-semibold text-gray-600 uppercase">Debit</th>
                          <th className="py-3 px-4 text-right text-xs font-semibold text-gray-600 uppercase">Credit</th>
                          <th className="py-3 px-4 text-right text-xs font-semibold text-gray-600 uppercase">Balance</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {statement.map((line, index) => (
                          <tr key={index}>
                            <td className="py-3 px-4 text-sm text-gray-900">{formatDate(line.date)}</td>
                            <td className="py-3 px-4 text-sm text-gray-900 font-medium">{line.reference}</td>
                            <td className="py-3 px-4 text-sm text-gray-700">{line.description}</td>
                            <td className="py-3 px-4 text-sm text-gray-900 text-right">
                              {line.debit > 0 ? formatCurrency(line.debit) : '-'}
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-900 text-right">
                              {line.credit > 0 ? formatCurrency(line.credit) : '-'}
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-900 text-right font-medium">
                              {formatCurrency(line.balance)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Footer */}
                    <div className="mt-12 pt-8 border-t border-gray-200 text-center">
                      <p className="text-sm text-gray-500">
                        Thank you for your business. Please settle any outstanding balance by the due date.
                      </p>
                    </div>
                  </div>
                </div>

                <style>{`
                  @media print {
                    @page { size: A4 landscape; margin: 0; }
                    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                  }
                `}</style>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
